// 🔔 데스크톱 알림 — 자체 프레임리스 팝업 창(notify.html) (2026-07-09 전면 교체).
// 기존 OS 토스트(tauri-plugin-notification)는 Windows 미등록 앱이라 클릭해도 앱이 활성화되지
// 않아 "눌러도 아무 반응 없음" — 자체 always-on-top 창은 클릭 처리(창 복원+방 이동)가 100% 가능.
// 일반 브라우저 환경에서는 모두 no-op — 기존 Web Notification API 가 폴백.

import { isTauri } from './env';
import { diagLog } from './diag';
import { getNotifPrefs, type NotifDesktopPos } from '../api/prefs';

export interface DesktopNotifyOptions {
	title: string;
	body?: string;
	icon?: string;
	roomId?: string;
	fileId?: string;
	avatarUrl?: string;   // 발신자 프로필 사진 (RC /avatar 프록시 절대 URL)
}

const POPUP_W = 384;
const POPUP_H = 104;
const EDGE_X = 14;       // 좌우 여백
const EDGE_TOP = 14;     // 위쪽 여백
const EDGE_BOTTOM = 58;  // 아래쪽 여백 — 작업표시줄

/** 팝업 좌상단 좌표(논리 px) — 모니터 논리 사각형과 설정 위치로 계산. 순수 함수(테스트용 export) */
export function popupPosition(pos: NotifDesktopPos, mon: { x: number; y: number; w: number; h: number }, size = { w: POPUP_W, h: POPUP_H }): { x: number; y: number } {
	const [v, h] = pos.split('-') as ['top' | 'bottom', 'left' | 'right' | 'center'];
	const x = h === 'left' ? mon.x + EDGE_X
		: h === 'center' ? mon.x + (mon.w - size.w) / 2
			: mon.x + mon.w - size.w - EDGE_X;
	const y = v === 'top' ? mon.y + EDGE_TOP : mon.y + mon.h - size.h - EDGE_BOTTOM;
	return { x: Math.round(x), y: Math.round(y) };
}

/**
 * 우하단 알림 팝업 창 발사 + deeplink 메타 등록(놓친 알림 후 창 복원 시 방 점프용).
 * 팝업 클릭 → notify.html 이 show_window + emit('tpt-notify-click') → 메인이 방으로 라우팅.
 * 반환: 발사 성공 여부 (= 브라우저 폴백을 멈춰야 하는지).
 */
let _lastPopupAt = 0;
let _pendingNotif: DesktopNotifyOptions | null = null;
let _pendingTimer = 0;
const COALESCE_MS = 1200;
// 🔒 생성 직렬화 + 메인 쪽 감시 (2026-10-02 형 "알림 팝업이 간혹 안 닫힘")
//   · 코얼레싱은 '시작 시각' 기준이라 IPC 가 1.2초 넘게 걸리면(절전 복귀 직후) 두 번째 호출이 통과해 같은 label 로
//     동시에 창을 만들었다 → 진행 중이면 최신 1건만 보관하고 끝난 뒤 이어서 띄운다.
//   · 닫는 장치(자동 닫힘·✕·클릭)가 전부 팝업 JS 안에만 있어 JS 가 안 뜬 좀비 창은 다음 알림까지 남았다 →
//     메인이 세대 번호로 40초 뒤 아직 같은 창이면 강제로 닫는다(팝업 자체 최대 수명 30초 + 여유).
let _popupInFlight = false;
let _popupGen = 0;
const WATCHDOG_MS = 40_000;
async function killNotifyWindow(): Promise<void> {
	try {
		const { WebviewWindow } = await import('@tauri-apps/api/webviewWindow');
		const w = await WebviewWindow.getByLabel('tpt-notify');
		if (w) await w.destroy().catch(() => w.close().catch(() => { /* */ }));
	} catch { /* 없음 */ }
}
export async function sendDesktopNotification(opts: DesktopNotifyOptions): Promise<boolean> {
	if (!isTauri()) return false;
	// 🪟 팝업 발사는 **메인 창 1곳**만 (2026-08-25 사용자 "관리자·채팅 팝아웃 3창 상태에서 알림이 안 닫힘").
	//   팝아웃(tpt-admin·tpt-room-*·tpt-calendar·tpt-ai)도 같은 앱 JS 를 돌려 각자 이 함수를 불렀다 → 같은 ms 에
	//   close→create 가 창 수만큼 겹치며(로그: popup start 2줄 + "label already exists") JS 가 뜨지 않은 좀비 팝업
	//   (타이머·닫기 버튼 부재)이 남았다. 코얼레싱 상태(_lastPopupAt)는 창별이라 창 사이 경합은 못 막는다.
	//   메인 창은 트레이로 숨겨져도 JS 가 살아 있으므로 여기서만 띄우면 충분하다. 팝아웃은 "처리됨"으로 답해
	//   브라우저 알림 폴백도 막는다(메인이 이미 띄우므로 중복).
	if (!isDesktopMainWindow()) { diagLog('notify:popup skipped (popout)', opts.title); return true; }
	// 🌊 폭주 코얼레싱 (2026-08-05 메일 알림 폭주 실사고) — close→create 연타 레이스로 JS 가 안 뜬
	//   좀비 팝업(닫기 불가)이 남았음. 1.2s 내 재발사는 즉시 띄우지 않는다.
	//   ⚠️ 예전엔 그냥 버려서 다른 방·다른 사람의 연속 알림이 통째로 사라졌다(딥링크도 최초 것만) →
	//   최신 1건을 보관해 창이 안정된 뒤 이어서 발사한다 (2026-08-12 데탑 알림 점검).
	const nowTs = Date.now();
	const wait = _popupInFlight ? COALESCE_MS : COALESCE_MS - (nowTs - _lastPopupAt);
	if (wait > 0) {
		_pendingNotif = opts;                       // 최신 것으로 교체(창은 1개라 마지막이 보이면 충분)
		if (!_pendingTimer) {
			_pendingTimer = window.setTimeout(() => {
				_pendingTimer = 0;
				const next = _pendingNotif;
				_pendingNotif = null;
				if (next) void sendDesktopNotification(next);
			}, wait + 60);
		}
		diagLog('notify:popup deferred', opts.title);
		return true;
	}
	_lastPopupAt = nowTs;
	_popupInFlight = true;
	diagLog('notify:popup start', opts.title);
	try {
		const [{ WebviewWindow }, { currentMonitor, getCurrentWindow, UserAttentionType }, { invoke }] = await Promise.all([
			import('@tauri-apps/api/webviewWindow'),
			import('@tauri-apps/api/window'),
			import('@tauri-apps/api/core'),
		]);

		// 🔔 작업표시줄 깜빡임 — 사용자가 확인(메인 창 포커스)하기 전까지 유지 (2026-07-20 요청).
		//   Windows: requestUserAttention(Critical) = FLASHW_ALL | FLASHW_TIMERNOFG → 창이 포그라운드로
		//   오면 자동 정지. 이미 포커스 중이면(사용자가 보고 있음) 깜빡일 필요 없어 skip.
		try {
			const mainWin = getCurrentWindow();
			if (!(await mainWin.isFocused())) await mainWin.requestUserAttention(UserAttentionType.Critical);
		} catch { /* 무해 — 깜빡임 실패해도 알림 팝업은 그대로 */ }

		// deeplink 메타 — 팝업이 자동 소멸한 뒤에라도 창 복원 시 최근 알림 방으로 점프 (기존 흐름 유지)
		if (opts.roomId || opts.fileId) {
			try { await invoke('register_deeplink', { meta: { roomId: opts.roomId, fileId: opts.fileId, ts: Date.now() } }); } catch { /* */ }
		}

		// 연속 알림 — 기존 팝업을 없애고 새로 (최신 내용 우선, 창 label 은 단일).
		//   🔴 close()/destroy() 는 요청만 보내고 돌아온다 — 곧바로 같은 label 로 만들면 "already exists" 로 생성이
		//   실패하거나, 소멸 중인 창 위에 새 페이지가 얹혀 JS 가 뜨지 않는 좀비가 됐다(2026-08-05·08-25 실사고).
		//   destroy(강제 — 좀비는 close 핸들러가 없다) 후 tauri://destroyed 를 최대 500ms 기다린다.
		try {
			const prev = await WebviewWindow.getByLabel('tpt-notify');
			if (prev) {
				await new Promise<void>((resolve) => {
					let done = false;
					const finish = () => { if (!done) { done = true; resolve(); } };
					void prev.once('tauri://destroyed', finish).catch(finish);
					prev.destroy().catch(() => prev.close().catch(finish));
					window.setTimeout(finish, 500);
				});
			}
		} catch { /* 없음 */ }

		// 배치 — 모니터 물리 크기/배율 → 논리 좌표. 위치는 개인설정(기본 우하단, 2026-09-04 원격 "표시위치를 지정").
		//   아래쪽은 작업표시줄 여유 58px, 위쪽은 14px. 모니터 미조회 시 OS 기본 위치.
		let x: number | undefined;
		let y: number | undefined;
		try {
			const mon = await currentMonitor();
			if (mon) {
				const sf = mon.scaleFactor || 1;
				const pos = popupPosition(getNotifPrefs().desktopPos, {
					x: mon.position.x / sf, y: mon.position.y / sf, w: mon.size.width / sf, h: mon.size.height / sf,
				});
				x = pos.x; y = pos.y;
			}
		} catch { /* 미조회 시 OS 기본 위치 */ }

		const qs = new URLSearchParams();
		qs.set('t', opts.title);
		if (opts.body) qs.set('b', opts.body);
		if (opts.roomId) qs.set('r', opts.roomId);
		if (opts.avatarUrl) qs.set('a', opts.avatarUrl);

		const win = new WebviewWindow('tpt-notify', {
			url: `notify.html?${qs.toString()}`,
			width: POPUP_W,
			height: POPUP_H,
			x, y,
			decorations: false,
			transparent: true,
			shadow: false,
			alwaysOnTop: true,
			skipTaskbar: true,
			resizable: false,
			focus: false,           // 타이핑 방해 금지 — 포커스 뺏지 않음
			visible: true,
		});
		// 생성 에러 — 같은 label 의 남은 창(좀비)이 원인일 수 있어 지운다. 다음 알림에서 재시도.
		void win.once('tauri://error', (e) => { diagLog('notify:window error', (e as { payload?: unknown }).payload); void killNotifyWindow(); });
		void win.once('tauri://created', () => diagLog('notify:window created'));
		const gen = ++_popupGen;
		window.setTimeout(() => {
			if (gen !== _popupGen) return;      // 그 사이 새 팝업이 떴다 — 그 팝업의 감시가 맡는다
			diagLog('notify:watchdog close');
			void killNotifyWindow();
		}, WATCHDOG_MS);
		return true;
	} catch (e) {
		diagLog('notify:popup failed', (e as Error).message || String(e));
		return false;
	} finally {
		_popupInFlight = false;
	}
}

/**
 * 알림 팝업 클릭 브릿지 — 팝업 창의 emit('tpt-notify-click') 을 받아
 * 메인 창 복원 후 기존 'tpt:open-room' 경로(채널 미로드 시 pending 보류)로 방 이동.
 * ChatPage 마운트 시 1회 등록. 반환: unlisten.
 */
export async function listenNotifyClick(): Promise<() => void> {
	if (!isTauri()) return () => { /* no-op */ };
	try {
		const [{ listen }, { invoke }] = await Promise.all([
			import('@tauri-apps/api/event'),
			import('@tauri-apps/api/core'),
		]);
		return await listen<{ roomId?: string }>('tpt-notify-click', (ev) => {
			void invoke('show_window').catch(() => { /* */ });
			const rid = ev.payload?.roomId;
			if (rid) {
				try { window.dispatchEvent(new CustomEvent('tpt:open-room', { detail: { roomId: rid } })); } catch { /* */ }
			}
		});
	} catch (e) {
		console.warn('[desktop] listenNotifyClick failed:', e);
		return () => { /* no-op */ };
	}
}

// 🪟 현재 JS 가 돌고 있는 창이 메인(label 'main')인가 — 팝아웃 창은 데탑 팝업·알림음을 내지 않는다.
//   label 은 모듈 로드 직후 비동기로 1회 조회해 캐시한다(호출부 notify() 가 동기). 조회 전/실패는 메인으로 간주해
//   알림이 사라지는 쪽으로 기울지 않게 한다. 팝아웃은 열린 뒤 수 초 안에 캐시가 채워지므로 실사용에 공백 없음.
let _isMainWindow: boolean | null = null;
if (isTauri()) {
	void import('@tauri-apps/api/window')
		.then(({ getCurrentWindow }) => { _isMainWindow = getCurrentWindow().label === 'main'; })
		.catch(() => { _isMainWindow = true; });
}
export function isDesktopMainWindow(): boolean {
	return _isMainWindow ?? true;
}
